import ActivateRecipe from "@/components/ActivateRecipe";
import AuxTabs from "@/components/AuxTabs";
import Button from "@/components/Button";
import DeactivateRecipe from "@/components/DeactivateRecipe";
import { ChefHat, Collection, Plan } from "@/components/Icons";
import Ingredients from "@/components/Ingredients";
import Logo from "@/components/LogoNew";
import Method from "@/components/Method";
import Newsletter from "@/components/Newsletter";
import Player from "@/components/Player";
import RecipeCTA from "@/components/RecipeCTA";
import RecipeTabs from "@/components/RecipeTabs";
import RedactedOverlay from "@/components/RedactedOverlay";
import { RecipeContext } from "@/contexts/RecipeContext";
import useCurrentUser from "@/hooks/useCurrentUser";
import useFaqs from "@/hooks/useFaqs";
import useGTM from "@/hooks/useGTM";
import usePayWall from "@/hooks/usePaywall";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import Ad from "@/src/ads/components/Ad";
import makeFaqSchema from "@/src/faqs/lib/makeFaqJsonLd";
import { useMediaQuery } from "@/src/hooks/useMediaQuery";
import clsx from "clsx";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { observer } from "mobx-react";
import Head from "next/head";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import React, { useContext, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

dayjs.extend(duration);

const Recipe = observer(
  ({ recipe, recipeEventData, globalFaqs, redacted = false, isLoading }) => {
    const { activeRecipe } = useContext(RecipeContext);
    const gtm = useGTM();

    const isRecipeActive = activeRecipe && activeRecipe.id === recipe.id;

    const relatedMusic = recipe.relatedMusic[0];

    const faqsToDisplay = useFaqs(
      globalFaqs.items,
      recipe.recipeFaqs,
      "recipes",
      false
    );

    const onRecipeCTAClicked = (e: MouseEvent) => {
      gtm.pushEvent("recipe_promo_box_clicked", recipeEventData);
    };

    // Remove salt and pepper from our ingredients list
    const filteredIngredients = recipe.recipeIngredients.filter((i) => {
      if (
        i.label === "Salt" ||
        i.label === "Black Pepper" ||
        i.label === "Pepper"
      ) {
        return i.quantity !== null;
      }
      return true;
    });

    recipe.recipeIngredients = filteredIngredients;

    return (
      <>
        {faqsToDisplay.length > 0 && (
          <Head>
            <script
              key={`recipeJSON-${recipe.id}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(makeFaqSchema(faqsToDisplay)),
              }}
            />
          </Head>
        )}
        <>
          <div
            className={clsx("Recipe__desktop", "hidden lg:block print:block")}
          >
            <div
              className={clsx(
                "mx-auto xl:container lg:max-w-[1050px] lg:pt-12 xl:max-w-full"
              )}
            >
              <div
                className={clsx(
                  "relative grid grid-cols-6 sm:gap-6 lg:gap-x-32"
                )}
              >
                <div
                  className={clsx(
                    "Recipe__desktopMethodColumn",
                    "col-span-3 print:col-span-6 print:row-start-2 print:pb-0"
                  )}
                >
                  {/* This ensures people can't just use web inspector to remove the lightbox and get recipes for free */}
                  {redacted ? (
                    <_PaywallRecipeCard
                      recipeAccessLevel={recipe.accessLevel}
                    />
                  ) : (
                    <_Main isRecipeActive={isRecipeActive} recipe={recipe} />
                  )}
                </div>
                <div
                  className={clsx(
                    "Recipe__desktopSidebarColumn",
                    "col-span-3 print:col-span-6 print:row-start-1 print:pb-0"
                  )}
                >
                  <_Sidebar
                    onRecipeCtaClicked={onRecipeCTAClicked}
                    relatedMusic={relatedMusic}
                    recipe={recipe}
                    faqs={faqsToDisplay}
                    faqIntro={globalFaqs.intro?.text}
                    redacted={redacted}
                    isLoading={isLoading}
                  />
                </div>
                <span
                  className={clsx(
                    "absolute bottom-16 left-1/2 top-0 -ml-[1px] block w-[1px] bg-zinc-200 print:hidden"
                  )}
                ></span>
              </div>
            </div>
          </div>
          <div
            className={clsx("Recipe__mobile", "block lg:hidden print:hidden")}
          >
            {recipe.ctaEnabled && (
              <div className={clsx("print:!hidden")}>
                <RecipeCTA
                  onCTAClick={onRecipeCTAClicked}
                  heading={recipe.ctaHeading}
                  content={recipe.ctaContent}
                  backgroundColor={recipe.ctaBackgroundColor}
                  color={recipe.ctaTextColor}
                  target={recipe.ctaTarget}
                  image={recipe.ctaImage?.[0]}
                />
              </div>
            )}
            <RecipeTabs
              recipe={recipe}
              ingredients={
                <>
                  <Ingredients
                    recipe={recipe}
                    redacted={redacted}
                    isLoading={isLoading}
                  />
                  {!!redacted && (
                    <div className={clsx("mt-6", {})}>
                      <_PaywallRecipeCard
                        recipeAccessLevel={recipe.accessLevel}
                        isMethod={false}
                      />
                    </div>
                  )}
                </>
              }
              method={
                <>
                  <_Main
                    isRecipeActive={isRecipeActive}
                    recipe={recipe}
                    redacted={redacted}
                  />
                  {!!redacted && (
                    <div className={clsx("mt-6 px-6", {})}>
                      <_PaywallRecipeCard
                        recipeAccessLevel={recipe.accessLevel}
                      />
                    </div>
                  )}
                </>
              }
              cta={
                <>
                  {recipe.ctaEnabled && (
                    <div className={clsx("mb-6 print:!hidden")}>
                      <RecipeCTA
                        onCTAClick={onRecipeCTAClicked}
                        heading={recipe.ctaHeading}
                        content={recipe.ctaContent}
                        backgroundColor={recipe.ctaBackgroundColor}
                        color={recipe.ctaTextColor}
                        target={recipe.ctaTarget}
                        image={recipe.ctaImage?.[0]}
                      />
                    </div>
                  )}
                </>
              }
            />
          </div>
        </>
        <Ad
          placementName={
            process.env.NEXT_PUBLIC_ADS_PROVIDER === "freestar"
              ? "mob.co.uk_leaderboard_3"
              : "htlad-ros_postarticle"
          }
          className="py-6 lg:py-12"
        />
      </>
    );
  }
);

const _Newsletter = () => {
  return <Newsletter source="recipe" />;
};

const _Main = ({ isRecipeActive, recipe, redacted = false }) => {
  return (
    <div
      className={clsx("Recipe__method", "relative px-6 xl:px-0 print:mb-8", {
        "max-h-[280px] overflow-hidden": redacted,
      })}
    >
      {redacted && <RedactedOverlay />}
      <div className={clsx("Recipe__header", "py-6 lg:pb-12 print:hidden")}>
        {isRecipeActive && (
          <div className={clsx("Recipe__deactivate", "absolute right-0 top-0")}>
            <DeactivateRecipe />
          </div>
        )}
        {!isRecipeActive && <ActivateRecipe recipe={recipe} />}
      </div>
      <div className={clsx("Recipe__steps", "pb-6 lg:pb-12")}>
        <Method recipe={recipe} isRecipeActive={isRecipeActive} />
      </div>
    </div>
  );
};

const _PaywallRecipeCard = ({ isMethod = true, recipeAccessLevel }) => {
  const router = useRouter();
  const [realIndex, setRealIndex] = useState(0);
  const swiperInstance = useRef(null);
  const gtm = useGTM();

  const { plan: userPlan, hasHadATrial } = useSubscriptionPermission();

  const isLargeScreen = useMediaQuery("(min-width: 1024px)");

  const { variants } = usePayWall();

  const content = isMethod
    ? variants?.inRecipeMethod
    : variants?.inRecipeIngredients;

  const iconOpts = {
    chefsHat: ChefHat,
    calendar: Plan,
    bookmark: Collection,
  };

  const title = {
    freeRecipe: "Unlock this free recipe",
    premiumRecipe: "Unlock Mob Premium",
  };

  return (
    <>
      <div
        className={clsx(
          "relative mx-auto w-full max-w-[600px] cursor-pointer rounded-3xl",
          {
            "bg-acid": userPlan === 0,
            "bg-rust": userPlan > 0,
          }
        )}
        onClick={() => {
          gtm.pushEvent("paywall_cta_clicked", {
            paywallType: "recipe",
            paywallHeading: content?.title,
          });

          posthog.capture("paywall_cta_clicked");

          if (recipeAccessLevel === "2" && (userPlan === 3 || userPlan === 4)) {
            router.push("/premium#sign-up");
          } else {
            router.push(`/onboarding`);
          }
        }}
      >
        <div
          className={clsx(
            "relative space-y-6 text-center",
            userPlan === 0 ? "text-zinc-800" : "text-white"
          )}
        >
          <div className="flex flex-col items-center px-6 pt-6">
            <div className="flex h-20 w-20 items-center justify-center">
              <Logo className="w-16" />
            </div>
            <h2
              dangerouslySetInnerHTML={{
                __html:
                  recipeAccessLevel === "1"
                    ? title.freeRecipe
                    : title.premiumRecipe,
              }}
              className={clsx(
                "font-spatial text-[1.375rem] font-bold leading-[1.2] tracking-[-0.5px] md:text-[2rem]"
              )}
            />
          </div>
          {content?.features.length && (
            <>
              {isLargeScreen ? (
                <ul className="space-y-2 px-6">
                  {content?.features.map((feature) => {
                    const Icon = feature.icon ? iconOpts[feature.icon] : null;

                    return (
                      <li
                        key={feature.text}
                        className={clsx(
                          "flex flex-col items-center space-y-1 rounded-xl border-2 p-4 text-base",
                          userPlan === 0 ? "border-black" : "border-white"
                        )}
                      >
                        {!!Icon && (
                          <Icon
                            size={32}
                            fill={userPlan === 0 ? "black" : "white"}
                          />
                        )}
                        <span>{feature.text}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <>
                  <Swiper
                    onSwiper={(swiper) => {
                      swiperInstance.current = swiper;
                    }}
                    onSlideChange={(swiper) => {
                      setRealIndex(swiper.realIndex);
                    }}
                  >
                    {content?.features.map((feature) => {
                      const Icon = iconOpts[feature.icon];

                      return (
                        <SwiperSlide key={feature.text}>
                          <li
                            className={clsx(
                              "mx-6 flex flex-col items-center space-y-1 rounded-xl border-2 p-4 text-base",
                              userPlan === 0 ? "border-black" : "border-white"
                            )}
                          >
                            {Icon && (
                              <Icon
                                size={32}
                                fill={userPlan === 0 ? "black" : "white"}
                              />
                            )}
                            <span>{feature.text}</span>
                          </li>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                  <ul className="flex items-center justify-center space-x-2 px-6">
                    {content?.features.map((feature, i) => (
                      <li key={`${feature.text}-dot`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            swiperInstance.current?.slideTo(i);
                          }}
                          className={clsx(
                            "flex h-2 w-2 rounded-full bg-[#E5E5E5] bg-opacity-30",
                            {
                              "h-3 w-3 bg-white !bg-opacity-100":
                                i === realIndex,
                            }
                          )}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2 px-6 pb-6">
            <Button
              label={"Try Mob for free"}
              theme="light"
              className="!h-auto w-full !px-6 !py-3 md:w-auto md:!px-12 md:!py-6 lg:w-full xl:w-auto"
              labelClassName="!text-base"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const _Sidebar = ({
  relatedMusic,
  onRecipeCtaClicked,
  recipe,
  faqs,
  faqIntro,
  redacted,
  isLoading,
}) => {
  const isLargeScreen = useMediaQuery("(min-width: 1024px)");
  const { isLoggedIn } = useCurrentUser();

  const className = clsx("Recipe__sidebar", "flex flex-col gap-y-8", {
    "Recipe__sidebar--withPlayer": !!relatedMusic,
  });

  return (
    <div className={className}>
      {recipe.ctaEnabled && (
        <div className={clsx("Recipe__recipeCta", "print:!hidden")}>
          <RecipeCTA
            onCTAClick={onRecipeCtaClicked}
            heading={recipe.ctaHeading}
            content={recipe.ctaContent}
            backgroundColor={recipe.ctaBackgroundColor}
            color={recipe.ctaTextColor}
            target={recipe.ctaTarget}
            image={recipe.ctaImage?.[0]}
          />
        </div>
      )}

      <div className={clsx("Recipe__ingredients")}>
        <Ingredients
          recipe={recipe}
          redacted={redacted}
          isLoading={isLoading}
        />
      </div>

      {process.env.NEXT_PUBLIC_ADS_PROVIDER === "hashtaglabs" && (
        <Ad placementName="htlad-recipe_ingedients" />
      )}
      {isLargeScreen && (
        <AuxTabs recipe={recipe} faqs={faqs} faqIntro={faqIntro} />
      )}

      {relatedMusic && (
        <div className={clsx("Recipe__player", "pb-4 print:!hidden")}>
          <Player spotifyUri={relatedMusic.spotifyUri} />
        </div>
      )}

      {!isLoggedIn && (
        <div
          className={clsx(
            "Recipe_descktopNewslatter",
            "rounded-2xl bg-zinc-950 p-6 text-sm text-zinc-50 @container print:hidden"
          )}
        >
          <_Newsletter />
        </div>
      )}
    </div>
  );
};

const _Feedback = ({ heading, children }) => {
  return (
    <div
      className={clsx(
        "Recipe__feedback",
        "w-full space-y-2 sm:w-1/2 lg:w-full print:hidden"
      )}
    >
      <h3 className={clsx("Recipe__feedbackHeading", "text-lg")}>{heading}</h3>
      <div className="Recipe__feedbackRating">{children}</div>
    </div>
  );
};

export default Recipe;
